import XLSX from 'xlsx-js-style';
import { rankModerators, getRatingColorLevel } from './periodSummaryRating';
import { MRP_METRICS } from './mrpStatisticsMetrics';

const STAT_LABELS = {
  mrp: 'Manager',
  junior: 'Junior',
};

const COLORS = {
  header: 'CFE2F3',
  white: 'FFFFFF',
  ratingBest: 'B6D7A8',
  ratingMid: 'FCE5CD',
  ratingWorst: 'F4CCCC',
};

const BORDER_ALL = {
  top: { style: 'thin', color: { rgb: '000000' } },
  bottom: { style: 'thin', color: { rgb: '000000' } },
  left: { style: 'thin', color: { rgb: '000000' } },
  right: { style: 'thin', color: { rgb: '000000' } },
};

function formatServerRu(server) {
  if (!server) return '—';
  const i = server.indexOf('_');
  return i >= 0 ? server.slice(0, i) : server;
}

function getMoscowNowStrings() {
  const now = new Date();
  const date = now.toLocaleDateString('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const time = now.toLocaleTimeString('ru-RU', {
    timeZone: 'Europe/Moscow',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return { date, time, full: `${date} ${time} МСК` };
}

function sanitizeFilePart(value) {
  return String(value || '')
    .replace(/[/\\:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim() || 'сводка';
}

function getColumns(statType) {
  const base = [
    { key: 'rank', header: '#', width: 6, get: (m) => m.rank },
    { key: 'server', header: 'Сервер', width: 8, get: (m) => formatServerRu(m.server) },
    { key: 'nickname', header: 'Список модераторов', width: 24, get: (m) => m.nickname || '—' },
    { key: 'static', header: 'Angry Space', width: 14, get: (m) => m.angry_space || '—' },
  ];
  const metricColumns = MRP_METRICS.map((metric) => ({
    key: metric.key,
    header: metric.label,
    width: 20,
    get: (m) => Number(m.totalMetrics?.[metric.key]) || 0,
  }));

  if (statType === 'mrp') {
    return [
      ...base,
      ...metricColumns,
      { key: 'reprimands', header: 'Выговоры', width: 10, get: (m) => m.totalReprimands || 0 },
      { key: 'rating', header: 'Рейтинг', width: 10, get: (m) => m.ratingLabel },
    ];
  }

  return [...base, ...metricColumns];
}

function buildSheetTitle(statType, periodType, year, month) {
  const typeLabel = STAT_LABELS[statType] || statType;
  const pad = (n) => String(n).padStart(2, '0');
  let from;
  let to;
  if (periodType === 'month') {
    const lastDay = new Date(year, month, 0).getDate();
    from = `${pad(1)}.${pad(month)}.${year}`;
    to = `${pad(lastDay)}.${pad(month)}.${year}`;
  } else {
    from = `01.01.${year}`;
    to = `31.12.${year}`;
  }
  return `Статистика ${typeLabel} с ${from} по ${to}`;
}

function getRatingFill(rating) {
  const level = getRatingColorLevel(rating);
  if (level === 'high') return COLORS.ratingBest;
  if (level === 'mid') return COLORS.ratingMid;
  if (level === 'low') return COLORS.ratingWorst;
  return COLORS.white;
}

function cellStyle({
  bold = false,
  fill = COLORS.white,
  align = 'center',
  wrap = false,
} = {}) {
  return {
    font: { name: 'Calibri', sz: 11, bold },
    fill: { patternType: 'solid', fgColor: { rgb: fill } },
    alignment: { horizontal: align, vertical: 'center', wrapText: wrap },
    border: BORDER_ALL,
  };
}

function setCell(ws, row, col, value, style) {
  const ref = XLSX.utils.encode_cell({ r: row, c: col });
  ws[ref] = { v: value, t: typeof value === 'number' ? 'n' : 's', s: style };
}

function buildStyledSheet(rows, statType, { periodType, year, month }) {
  const columns = getColumns(statType);
  const columnCount = columns.length;
  const ratingColIndex = columns.findIndex((column) => column.key === 'rating');

  const title = buildSheetTitle(statType, periodType, year, month);
  const ws = {};
  const totalRows = 2 + rows.length;

  setCell(ws, 0, 0, title, cellStyle({ bold: true, fill: COLORS.header, align: 'center' }));

  columns.forEach((column, colIndex) => {
    setCell(ws, 1, colIndex, column.header, cellStyle({ bold: true, fill: COLORS.header }));
  });

  rows.forEach((row, rowIndex) => {
    const sheetRow = rowIndex + 2;
    columns.forEach((column, colIndex) => {
      const value = column.get(row);
      const fill =
        colIndex === ratingColIndex && ratingColIndex >= 0
          ? getRatingFill(row.rating)
          : COLORS.white;
      setCell(ws, sheetRow, colIndex, value, cellStyle({ fill }));
    });
  });

  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: totalRows - 1, c: columnCount - 1 },
  });
  ws['!cols'] = columns.map((column) => ({ wch: column.width }));
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: columnCount - 1 } }];
  ws['!freeze'] = {
    xSplit: 0,
    ySplit: 2,
    topLeftCell: 'A3',
    activePane: 'bottomLeft',
    state: 'frozen',
  };
  ws['!rows'] = [{ hpt: 28 }, { hpt: 22 }];

  return { ws, columns };
}

function buildFileName({ statType, periodLabel, periodType, allTypes }) {
  const { date, time } = getMoscowNowStrings();
  const typePart = allTypes ? 'все типы' : STAT_LABELS[statType] || statType;
  const periodPart = periodType === 'month' ? periodLabel : `год ${periodLabel}`;
  return `${sanitizeFilePart(`Сводка ${typePart} ${periodPart}`)} — ${date} ${time.replace(/:/g, '-')}.xlsx`;
}

function sheetNameForStatType(statType) {
  return (STAT_LABELS[statType] || statType).slice(0, 31);
}

function writeWorkbook(wb, fileName) {
  XLSX.writeFile(wb, fileName);
}

/**
 * Один лист — текущий тип статистики
 */
export function exportPeriodSummaryToExcel({
  moderators,
  statType,
  ratingOpts,
  periodLabel,
  periodType,
  year,
  month,
}) {
  const rows = rankModerators(moderators, ratingOpts);
  if (!rows.length) return false;

  const { ws } = buildStyledSheet(rows, statType, {
    periodType,
    year: year ?? ratingOpts.year,
    month: month ?? ratingOpts.month,
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetNameForStatType(statType));
  writeWorkbook(wb, buildFileName({ statType, periodLabel, periodType, allTypes: false }));
  return true;
}

/**
 * Книга с листами MRP, MCL, FAMQ, GZ, Media
 */
export function exportPeriodSummaryWorkbook({
  sheets,
  periodLabel,
  periodType,
  year,
  month,
}) {
  const wb = XLSX.utils.book_new();
  let hasData = false;
  const periodYear = year ?? sheets[0]?.ratingOpts?.year;
  const periodMonth = month ?? sheets[0]?.ratingOpts?.month;

  sheets.forEach(({ statType, moderators, ratingOpts }) => {
    const rows = rankModerators(moderators, ratingOpts);
    if (!rows.length) return;
    hasData = true;
    const { ws } = buildStyledSheet(rows, statType, {
      periodType,
      year: periodYear ?? ratingOpts.year,
      month: periodMonth ?? ratingOpts.month,
    });
    XLSX.utils.book_append_sheet(wb, ws, sheetNameForStatType(statType));
  });

  if (!hasData) return false;

  writeWorkbook(wb, buildFileName({ periodLabel, periodType, allTypes: true }));
  return true;
}
